import { Box } from "@chakra-ui/layout";
import { DropZone, EditButton, RemoveButton } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useDeleteUploadedFileMutation } from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { Preview } from "@src/utils/components/dropzone/Preview";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React, {
  Fragment,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import type { FileInputForm } from "../../form/FileInputForm";
import { Loading } from "./Loading";

interface UploadFileProps {
  value: FileInputForm;
  onDropFiles: (files: File[]) => void;
  onRemoveFile: () => void;
}

const UploadFile = forwardRef<HTMLInputElement, UploadFileProps>(
  function UploadFile({ value, onDropFiles, onRemoveFile }, fileInputRef) {
    const [removeUploadedFile, { loading: removeLoading }] =
      useDeleteUploadedFileMutation({
        onCompleted({ deleteUploadedFile }) {
          if (deleteUploadedFile) {
            value.unsetExistingFile();
            onRemoveFile();
          }
        },
        ...onError(),
      });

    const removeFile = () => {
      if (value.fileToUpload.value) {
        value.fileToUpload.reset();
        onRemoveFile();
      } else if (value.existingFile) {
        removeUploadedFile({ variables: { id: value.existingFile.id } });
      }
    };

    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      fileInputRef,
      () => inputRef.current,
    );

    return (
      <div>
        {value.file && (
          <div className="flex space-x-6 mb-6">
            {can("expense_deleteFiles") && (
              <React.Fragment>
                <EditButton
                  onClick={() => inputRef.current?.click()}
                >{t`Change file`}</EditButton>
                <RemoveButton colorScheme="red" onClick={removeFile}>
                  <Trans>Delete file</Trans>
                </RemoveButton>
              </React.Fragment>
            )}
            {value.fileToUpload.value && (
              <p className="pl-4 font-normal text-xl">
                {value.fileToUpload.value.size} <Trans>bytes</Trans>
              </p>
            )}
          </div>
        )}

        {removeLoading ? (
          <Loading />
        ) : (
          <Fragment>
            {value.file && <Preview media={value.file} />}
            <Box
              style={
                value.file
                  ? { opacity: 0, width: 0, height: 0, overflow: "hidden" }
                  : {}
              }
            >
              <DropZone
                ref={inputRef}
                allowMultiple
                accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, application/msword, text/plain, image/jpeg, image/png"
                onDrop={onDropFiles}
              />
            </Box>
          </Fragment>
        )}
      </div>
    );
  },
);

export default observer(UploadFile);
